import React, { useCallback, useMemo, useState } from 'react';

import { SUBCATEGORIES } from '../../common/categories';
import * as actions from '../../containers/Integrations/integrationsActions';

import ADFSApp from './ADFSApp';
import ArrayContainer from '../Controls/ArrayContainer/ArrayContainer';
import Button from '../Controls/Button/Button';
import ImportRelyingParties from './ImportRelyingParties';
import { downloadFile } from '../../../utils';
import LightTooltip from '../../../Controls/LightTooltip';
import downloadIcon from '../../../common/NewImages/download-icon.svg';
import uploadIcon from '../../../common/NewImages/upload-icon.svg';
import { useGetTabDisplay } from '../../hooks/lockedFeatures/useTabDisplay';
import { SettingsFeatureLock } from '../LockedFeatures/SettingsFeatureLock';

const { ADFS_FEDERATED_APPS } = SUBCATEGORIES;

const ADFSApps = ({ fields, dispatch, authorizePermission }) => {
    const [showImportModal, setShowImportModal] = useState(false);
    const { enabled } = useGetTabDisplay(ADFS_FEDERATED_APPS.ID);

    const adfsApps = fields[ADFS_FEDERATED_APPS.ID];

    const disabled = useMemo(() => !authorizePermission || !enabled, [authorizePermission, enabled]);

    const toggleImportPopup = useCallback(() => {
        setShowImportModal(!showImportModal);
    }, [showImportModal, setShowImportModal]);

    const downloadExportRelyingPartiesPs1 = () => {
        try {
            downloadFile('/Settings/DownloadExportRelyingPartiesPs1');
        } catch (err) {
            console.error('An error occurred while trying to download the Export Relying Parties Ps1.');
        }
    };

    const dispatchAdd = useCallback(() => {
        dispatch({ type: actions.ADD_ADFS_APP });
    }, [dispatch]);

    const dispatchUpdate = useCallback(
        (index, fieldId, value) => {
            dispatch({
                type: actions.UPDATE_ADFS_APP,
                payload: { index, arrFieldId: fieldId, value }
            });
        },
        [dispatch]
    );

    const dispatchRemove = useCallback(
        (index) => {
            dispatch({ type: actions.REMOVE_ADFS_APP, payload: index });
        },
        [dispatch]
    );

    const onImport = useCallback(
        (adfsApps) => {
            dispatch({ type: actions.ADD_MULTIPLE_ADFS_APPS, payload: adfsApps });
        },
        [dispatch]
    );

    return (
        <>
            {adfsApps.value.map((app, index) => {
                return (
                    <ArrayContainer
                        key={ADFS_FEDERATED_APPS.ID + index}
                        header="ADFS App"
                        remove={() => !disabled && dispatchRemove(index)}>
                        <ADFSApp
                            adfsApp={app}
                            changedFunc={(fId, v) => dispatchUpdate(index, fId, v)}
                            disabled={disabled}
                        />
                    </ArrayContainer>
                );
            })}

            <div class={`${adfsApps.value.length === 0 ? 'mt-12 ' : ''} flex`}>
                <div>
                    <SettingsFeatureLock id={SUBCATEGORIES.ADFS_FEDERATED_APPS.ID} isTabId={true}>
                        <Button id="adfs-add-federated-app-btn" onClick={dispatchAdd} disabled={disabled}>
                            <div className="flex">+ Add Federated App</div>
                        </Button>
                    </SettingsFeatureLock>
                </div>
                <div>
                    <SettingsFeatureLock id={SUBCATEGORIES.ADFS_FEDERATED_APPS.ID} isTabId={true}>
                        <Button style={{ marginLeft: '3rem' }} onClick={toggleImportPopup} disabled={disabled}>
                            <div className="flex gap-1">
                                <img src={uploadIcon} alt="" />
                                <div>Import From File</div>
                            </div>
                        </Button>
                    </SettingsFeatureLock>
                </div>
                <div>
                    <SettingsFeatureLock id={SUBCATEGORIES.ADFS_FEDERATED_APPS.ID} isTabId={true}>
                        <LightTooltip
                            title={
                                <div>
                                    The ADFS utility script retrieves all relying parties (applications) registered for
                                    the ADFS server. The utility script output can then be uploaded to the Admin Console
                                    to protect parties using SAML policies.
                                </div>
                            }
                            enterDelay={100}
                            placement={'top'}
                            disableFocusListener={disabled}
                            disableHoverListener={disabled}
                            disableTouchListener={disabled}>
                            <span>
                                <Button
                                    style={{ marginLeft: '3rem' }}
                                    onClick={downloadExportRelyingPartiesPs1}
                                    disabled={disabled}>
                                    <div className="flex gap-1">
                                        <img src={downloadIcon} alt="" />

                                        <div>Download Utility Script</div>
                                    </div>
                                </Button>
                            </span>
                        </LightTooltip>
                    </SettingsFeatureLock>
                </div>
            </div>

            {showImportModal && <ImportRelyingParties save={onImport} close={toggleImportPopup} />}
        </>
    );
};

export default React.memo(ADFSApps);
